import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '99+';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   *
   * Cart bubble collapses Case groups (line items sharing a `_case_id`
   * property) into a single tally — a 4-wine case shows as "1", not "4".
   * Shopify's raw `item_count` doesn't know about that grouping, so we
   * re-fetch /cart.js and recount from the line items themselves. The
   * server-rendered count uses the same rule (`snippets/cart-bubble.liquid`).
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    try {
      const res = await fetch('/cart.js', { cache: 'no-store' });
      if (!res.ok) throw new Error('cart fetch failed');
      const cart = await res.json();
      const itemCount = CartIcon.#countCollapsed(cart.items || []);
      // Cases-collapsed counts are absolute; the product-form `add to current`
      // semantic doesn't apply (we'd double-count). Force replace.
      this.renderCartBubble(itemCount, false);
    } catch (_) {
      // Fallback to raw item_count from the event payload.
      const itemCount = event.detail.data?.itemCount ?? 0;
      this.renderCartBubble(itemCount, comingFromProductForm);
    }
  };

  /**
   * Sum of cart bubble units — one per unique `_case_id` group, plus the
   * quantities of any non-case line items.
   * @param {Array<{quantity: number, properties?: Record<string, string>}>} items
   * @returns {number}
   */
  static #countCollapsed(items) {
    let count = 0;
    const seen = new Set();
    for (const item of items) {
      const cid = item.properties?._case_id;
      if (cid) {
        if (seen.has(cid)) continue;
        seen.add(cid);
        count += 1;
      } else {
        count += item.quantity || 0;
      }
    }
    return count;
  }

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
